import { toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { changePassword } from 'modules/profile/change-password/api/change-password-api'
import TwoFactorAuth from 'modules/profile/change-password/components/two-factor-auth'
import { ChangePasswordLoco } from 'modules/profile/change-password/constants/change-password-loco'
import {
  ChangePasswordEditableInterface,
  ChangePasswordErrorResponseContentInterface,
  ChangePasswordErrorsInterface,
  ChangePasswordInterface,
} from 'modules/profile/change-password/types/change-password-interface'
import SettingsLayout from 'modules/profile/components/settings-layout'
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys'
import { NextPage } from 'next'
import React, { useState } from 'react'
import FormInputPassword from 'shared/components/form-input/form-input-password'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'

const defaultErrors: ChangePasswordErrorsInterface = {
  first: '',
  second: '',
}

const defaultValues: ChangePasswordEditableInterface = {
  password: '',
  confirmPassword: '',
}

const ChangePassword: NextPage = () => {
  const { t } = useLocoTranslation()

  const [tempState, setTempState] = useState<ChangePasswordEditableInterface>(defaultValues)

  const [errors, setErrors] = useState<ChangePasswordErrorsInterface>(defaultErrors)

  const [isFetching, setIsFetching] = useState(false)

  const onChangePassword = async () => {
    try {
      const data: ChangePasswordInterface = {
        password: { first: tempState.password, second: tempState.confirmPassword },
      }
      setErrors(defaultErrors)
      setIsFetching(true)
      await changePassword(data)
      setIsFetching(false)
      setTempState(defaultValues)
      toast.success(t('global.changes_saved'))
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        const changePasswordErrors =
          e.errors as unknown as ChangePasswordErrorResponseContentInterface
        setErrors({
          first: changePasswordErrors.fields.password?.first?.join(''),
          second: changePasswordErrors.fields.password?.second?.join(''),
        })
      } else {
        toast.error(t('global.error'))
      }
    }
  }
  return (
    <SettingsLayout
      titles={[{ caption: t(settingsMenuLocoKeys.change_password), isStatic: true }]}
      isFetching={isFetching}
      onSave={onChangePassword}
      disabled={tempState.password !== tempState.confirmPassword || tempState.password === ''}
    >
      <div
        className={
          'bg-background-secondary flex h-fit w-full flex-col gap-7 overflow-x-hidden rounded-md p-5 lg:gap-10 '
        }
      >
        <TwoFactorAuth />
        <form
          className="flex flex-col  gap-7 lg:gap-10 [&>*]:flex-1"
          id="login"
          action="signup.php"
          method="post"
        >
          <FormInputPassword
            label={t('dashboard.settings.change_password.new_password')}
            type={'password'}
            required
            autoComplete={'new-password'}
            id="new-password"
            className={'max-w-[400px]'}
            value={tempState.password}
            onChange={e =>
              setTempState(prev => ({
                ...prev,
                password: e.target.value,
              }))
            }
            error={errors.first}
          />
          <FormInputPassword
            label={t('dashboard.settings.change_password.confirm_new_password')}
            type={'password'}
            autoComplete={'new-password'}
            id="confirm-new-password"
            required
            className={'max-w-[400px]'}
            value={tempState.confirmPassword}
            onChange={e => setTempState(prev => ({ ...prev, confirmPassword: e.target.value }))}
            error={errors.second}
          />
        </form>
      </div>
    </SettingsLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(ChangePasswordLoco, 'global.settings')

export default ChangePassword
